import { API_BASE_URL } from "../configs/api";

const LIGHT_PUNCTUATION_PATTERN = /[\s_\-\u2013\u2014/()[\]{}.,:;'"&]+/g;
const LIGHT_PUNCTUATION_CHAR_PATTERN =
  /[\s_\-\u2013\u2014/()[\]{}.,:;'"&]/u;
const LETTER_OR_NUMBER_PATTERN = /[\p{L}\p{N}]/u;

let backendOperationMetadataPromises = new Map();

export function normalizeForOperationMatch(value) {
  return String(value ?? "")
    .toLocaleLowerCase("en-US")
    .replace(LIGHT_PUNCTUATION_PATTERN, " ")
    .trim();
}

export const normalizeOperationPhrase = normalizeForOperationMatch;

function normalizePromptWithIndexMap(value) {
  const text = String(value ?? "");
  let normalized = "";
  const starts = [];
  const ends = [];

  for (let index = 0; index < text.length; ) {
    const character = String.fromCodePoint(text.codePointAt(index));
    const width = character.length;

    if (LIGHT_PUNCTUATION_CHAR_PATTERN.test(character)) {
      const separatorStart = index;
      let separatorEnd = index + width;
      index = separatorEnd;

      while (index < text.length) {
        const nextCharacter = String.fromCodePoint(text.codePointAt(index));
        if (!LIGHT_PUNCTUATION_CHAR_PATTERN.test(nextCharacter)) break;
        index += nextCharacter.length;
        separatorEnd = index;
      }

      if (normalized && !normalized.endsWith(" ")) {
        normalized += " ";
        starts.push(separatorStart);
        ends.push(separatorEnd);
      }
      continue;
    }

    const lowerCharacter = character.toLocaleLowerCase("en-US");
    normalized += lowerCharacter;
    for (let offset = 0; offset < lowerCharacter.length; offset += 1) {
      starts.push(index);
      ends.push(index + width);
    }
    index += width;
  }

  if (normalized.endsWith(" ")) {
    normalized = normalized.slice(0, -1);
    starts.pop();
    ends.pop();
  }

  return { normalized, starts, ends };
}

export function findOperationHints(prompt, operations = []) {
  const text = String(prompt ?? "");
  const normalizedPrompt = normalizePromptWithIndexMap(text);
  const candidates = [];
  const canonicalOperations = [
    ...new Map(
      operations
        .filter((operation) => typeof operation === "string" && operation.trim())
        .map((operation) => [normalizeForOperationMatch(operation), operation]),
    ).values(),
  ];

  for (const operation of canonicalOperations) {
    const normalizedOperation = normalizeForOperationMatch(operation);
    if (!normalizedOperation) continue;

    let searchIndex = 0;
    while (searchIndex <= normalizedPrompt.normalized.length) {
      const matchIndex = normalizedPrompt.normalized.indexOf(
        normalizedOperation,
        searchIndex,
      );
      if (matchIndex === -1) break;

      const matchEnd = matchIndex + normalizedOperation.length;
      const hasLeftBoundary =
        matchIndex === 0 ||
        !LETTER_OR_NUMBER_PATTERN.test(
          normalizedPrompt.normalized[matchIndex - 1],
        );
      const hasRightBoundary =
        matchEnd === normalizedPrompt.normalized.length ||
        !LETTER_OR_NUMBER_PATTERN.test(normalizedPrompt.normalized[matchEnd]);

      if (hasLeftBoundary && hasRightBoundary) {
        const start = normalizedPrompt.starts[matchIndex];
        const end = normalizedPrompt.ends[matchEnd - 1];
        candidates.push({
          operation,
          matched_text: text.slice(start, end),
          start,
          end,
          match_type: "exact_operation_phrase",
          normalized_length: normalizedOperation.length,
        });
      }

      searchIndex = matchIndex + 1;
    }
  }

  candidates.sort(
    (left, right) =>
      right.normalized_length - left.normalized_length ||
      right.matched_text.length - left.matched_text.length ||
      left.start - right.start,
  );

  const selected = [];
  for (const candidate of candidates) {
    const overlapsSelected = selected.some(
      (hint) => candidate.start < hint.end && candidate.end > hint.start,
    );
    if (!overlapsSelected) selected.push(candidate);
  }

  return selected
    .sort((left, right) => left.start - right.start)
    .map(({ normalized_length, ...hint }) => hint);
}

export const detectExactOperationHints = findOperationHints;

function operationNamesFromMetadata(payload) {
  const entries = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.operations)
      ? payload.operations
      : [];

  return [
    ...new Set(
      entries
        .map((entry) => {
          if (typeof entry === "string") return entry.trim();
          const name =
            entry?.operation ||
            entry?.name ||
            entry?.Operation ||
            entry?.operation_name;
          return typeof name === "string" ? name.trim() : "";
        })
        .filter(Boolean),
    ),
  ];
}

export function getBackendOperationMetadata({
  apiBase = API_BASE_URL,
  fetchImpl = fetch,
} = {}) {
  const normalizedApiBase = String(apiBase ?? "").replace(/\/$/, "");
  const useSharedCache = fetchImpl === fetch;
  if (
    useSharedCache &&
    backendOperationMetadataPromises.has(normalizedApiBase)
  ) {
    return backendOperationMetadataPromises.get(normalizedApiBase);
  }

  const request = (async () => {
    const response = await fetchImpl(
      `${normalizedApiBase}/api/metadata/operations`,
    );
    if (!response.ok) {
      throw new Error(`Operation metadata request failed (${response.status}).`);
    }
    return operationNamesFromMetadata(await response.json());
  })().catch(() => []);

  if (useSharedCache) {
    backendOperationMetadataPromises.set(normalizedApiBase, request);
  }
  return request;
}

export const fetchOperationCatalog = getBackendOperationMetadata;

export function clearOperationCatalogCacheForTests() {
  backendOperationMetadataPromises = new Map();
}

export function buildChatRequestPayload(
  prompt,
  operations = [],
  { summarize = 1 } = {},
) {
  const operationHints = findOperationHints(prompt, operations);
  return {
    prompt,
    summarize,
    ...(operationHints.length > 0 ? { operation_hints: operationHints } : {}),
  };
}